export interface SystemTemplate {
  template_id: string
  scope: 'system'
  tenant_id: null
  workspace_id: null
  industry: string | null
  decision_type: string
  name: string
  description: string
  default_question: string
  default_business_context: string
  default_stimuli_prompts: string[]
  recommended_segments: string[]
  recommended_passes: string[]
}

export const SYSTEM_TEMPLATES: SystemTemplate[] = [
  {
    template_id: 'tpl_concept_test',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: null,
    decision_type: 'concept_test',
    name: 'Concept Test',
    description: 'Compare how a target segment responds to two or more product or service concepts.',
    default_question: 'How would [segment] react to this concept compared to alternatives?',
    default_business_context: 'We are testing which concept resonates most before committing to development. Key decision: proceed with Concept A, B, or C.',
    default_stimuli_prompts: [
      'Concept A — description, key benefit, and proof point',
      'Concept B — description, key benefit, and proof point',
      'Concept C (optional) — description, key benefit, and proof point',
    ],
    recommended_segments: ['Primary target segment', 'Secondary segment'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'COUNTERFACTUAL_RISK', 'QUANTITATIVE_DIST', 'SKEPTICAL_RESEARCHER', 'VALIDATION_PLANNER'],
  },
  {
    template_id: 'tpl_message_test',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: null,
    decision_type: 'message_test',
    name: 'Message Test',
    description: 'Determine which message or headline is most likely to convert or resonate with a given segment.',
    default_question: 'Which message is more likely to convert [segment]?',
    default_business_context: 'We are finalizing copy before launch. Key decision: which headline / subject line / body copy variant to ship.',
    default_stimuli_prompts: [
      'Message A — full headline + body copy',
      'Message B — full headline + body copy',
      'Message C (optional) — full headline + body copy',
    ],
    recommended_segments: ['Primary audience segment'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'COUNTERFACTUAL_RISK', 'VALIDATION_PLANNER'],
  },
  {
    template_id: 'tpl_pricing_reaction',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: null,
    decision_type: 'pricing_reaction',
    name: 'Pricing Reaction',
    description: 'Test how a segment responds to a pricing tier, price change, or pricing communication.',
    default_question: 'How does [segment] respond to this pricing tier / change?',
    default_business_context: 'We are considering a pricing adjustment. Key decision: proceed, modify the framing, or delay.',
    default_stimuli_prompts: [
      'Current pricing — full price + framing',
      'Proposed new pricing — full price + framing',
      'Alternative tier or framing (optional)',
    ],
    recommended_segments: ['Price-sensitive segment', 'Value-focused segment'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'QUANTITATIVE_DIST', 'COUNTERFACTUAL_RISK', 'SKEPTICAL_RESEARCHER', 'VALIDATION_PLANNER'],
  },
  {
    template_id: 'tpl_claims_renewal_copy',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: 'insurance',
    decision_type: 'claims_renewal_copy',
    name: 'Claims / Renewal Copy',
    description: 'Check whether renewal or claims communications land as trustworthy and clear — and flag compliance risks before send.',
    default_question: 'Does this renewal/claims communication land as trustworthy and clear for [segment]?',
    default_business_context: 'We are finalising renewal or claims copy before distribution. Industry compliance rules apply.',
    default_stimuli_prompts: [
      'Renewal notice draft — full text',
      'Claims communication draft — full text',
    ],
    recommended_segments: ['General policyholders', 'Recent claimants'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'COUNTERFACTUAL_RISK', 'QUANTITATIVE_DIST', 'SKEPTICAL_RESEARCHER', 'VALIDATION_PLANNER'],
  },
  {
    template_id: 'tpl_agent_script',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: null,
    decision_type: 'agent_script',
    name: 'Agent Script',
    description: 'Evaluate how a target segment responds to an agent-led conversation: opening, objection handling, and close.',
    default_question: 'How would [segment] respond to this agent-led conversation script?',
    default_business_context: 'We are training agents and finalising their script. Key decision: which script variant produces the best response.',
    default_stimuli_prompts: [
      'Script — opening (full text)',
      'Script — objection handling section (full text)',
      'Script — close (full text)',
    ],
    recommended_segments: ['Warm prospects', 'Cold prospects'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'COUNTERFACTUAL_RISK', 'VALIDATION_PLANNER'],
  },
  {
    template_id: 'tpl_churn_upgrade',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: 'saas',
    decision_type: 'churn_upgrade',
    name: 'Churn / Upgrade',
    description: 'Validate what is actually driving churn or upgrade decisions — surface the gap between stated and behavioral drivers.',
    default_question: 'What is actually driving churn or upgrade intent in [segment]?',
    default_business_context: 'We need to understand true churn/upgrade drivers before adjusting retention strategy or pricing.',
    default_stimuli_prompts: [
      'Current retention message / win-back offer',
      'Alternative win-back approach',
    ],
    recommended_segments: ['At-risk churners', 'Recent churners', 'Upgrade candidates'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'COUNTERFACTUAL_RISK', 'SKEPTICAL_RESEARCHER', 'VALIDATION_PLANNER'],
  },
  {
    template_id: 'tpl_onboarding',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: null,
    decision_type: 'onboarding',
    name: 'Onboarding',
    description: 'Test whether an onboarding flow is clear, motivating, and activates the right behaviours in new users.',
    default_question: 'Is this onboarding flow clear and motivating for [segment]?',
    default_business_context: 'We are A/B testing or finalising our onboarding sequence before rollout.',
    default_stimuli_prompts: [
      'Welcome email — full text',
      'Day 1 in-product onboarding — key screens + copy',
      'Day 3 nudge message',
    ],
    recommended_segments: ['New users — SMB', 'New users — Enterprise'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'COUNTERFACTUAL_RISK', 'VALIDATION_PLANNER'],
  },
  {
    template_id: 'tpl_feature_launch_comms',
    scope: 'system',
    tenant_id: null,
    workspace_id: null,
    industry: null,
    decision_type: 'feature_launch_comms',
    name: 'Feature Launch Comms',
    description: 'Predict how a segment will receive a feature announcement across different channels.',
    default_question: 'How will [segment] receive this feature announcement?',
    default_business_context: 'We are preparing a feature launch. Key decision: which announcement variant drives the most activation.',
    default_stimuli_prompts: [
      'Launch email — subject + body',
      'In-product announcement — header + body',
      'Blog post excerpt (optional)',
    ],
    recommended_segments: ['Power users', 'Casual users', 'Churned users'],
    recommended_passes: ['EVIDENCE_GROUNDED', 'SEGMENT_RESPONSE', 'BEHAVIORAL_DRIVER', 'COUNTERFACTUAL_RISK', 'QUANTITATIVE_DIST', 'VALIDATION_PLANNER'],
  },
]
